import { Constants } from "@eagerdog/constants";
import { IEventRegisterRow } from "../../../services/helper.service.js";
import { IShow } from "@eagerdog/interfaces";

export namespace AKCConformation {
  export const getForm = (show?: IShow): IEventRegisterRow[] => {
    // Helper function to check if an element/level is available in the show
    const isElementAvailable = (elementOrLevel: string): boolean => {
      if (!show?.show_elements) return true; // If no show provided, show all options
      
      return show.show_elements.some(se => 
        se.show_element === elementOrLevel || se.level === elementOrLevel
      );
    };

    // Filter regular conformation options (single select)
    const regularConformationOptions = [
      {
        value: Constants.dog_class_element_level.akc_4_6_month_puppy,
        id: Constants.dog_class_element_level.akc_4_6_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_6_9_month_puppy,
        id: Constants.dog_class_element_level.akc_6_9_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_9_12_month_puppy,
        id: Constants.dog_class_element_level.akc_9_12_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_12_18_month_puppy,
        id: Constants.dog_class_element_level.akc_12_18_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_18_24_month_puppy,
        id: Constants.dog_class_element_level.akc_18_24_month_puppy,
      },
      {
        value: Constants.dog_class_element_level.akc_coursing,
        id: Constants.dog_class_element_level.akc_coursing,
      },
      {
        value: Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
        id: Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
      },
      {
        value:
          Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
        id: Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
      },
      {
        value: Constants.dog_class_element_level.akc_amateur_owner_handler,
        id: Constants.dog_class_element_level.akc_amateur_owner_handler,
      },
      {
        value: Constants.dog_class_element_level.akc_american_bred,
        id: Constants.dog_class_element_level.akc_american_bred,
      },
      {
        value: Constants.dog_class_element_level.akc_open,
        id: Constants.dog_class_element_level.akc_open,
      },
      {
        value: Constants.dog_class_element_level.akc_best_of_breed,
        id: Constants.dog_class_element_level.akc_best_of_breed,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_6_8_years,
        id: Constants.dog_class_element_level.akc_veteran_6_8_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_10_years,
        id: Constants.dog_class_element_level.akc_veteran_8_10_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_10_12_years,
        id: Constants.dog_class_element_level.akc_veteran_10_12_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_7_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_8_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_8_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_13_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_13_plus_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_7_10_years,
        id: Constants.dog_class_element_level.akc_veteran_7_10_years,
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_12_plus_years,
        id: Constants.dog_class_element_level.akc_veteran_12_plus_years,
      },
      {
        value:
          Constants.dog_class_element_level.akc_junior_showmanship,
        id: Constants.dog_class_element_level.akc_junior_showmanship,
      },
    ].filter(option => isElementAvailable(option.value));

    // Filter special conformation options (multi-select alongside regular)
    const specialConformationOptions = [
      {
        value: Constants.dog_class_element_level.akc_brood_bitch,
        id: Constants.dog_class_element_level.akc_brood_bitch,
      },
      {
        value: Constants.dog_class_element_level.akc_stud_dog,
        id: Constants.dog_class_element_level.akc_stud_dog,
      },
      {
        value: Constants.dog_class_element_level.akc_brace,
        id: Constants.dog_class_element_level.akc_brace,
      },
    ].filter(option => isElementAvailable(option.value));

    // Filter non-licensed options based on available show elements
    const nonLicensedOptions = [
      {
        value: Constants.dog_class_element_level.akc_puppy_sweepstakes,
        id: Constants.dog_class_element_level.akc_puppy_sweepstakes,
        requiresPuppyClass: true, // for validation
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        minAgeMonths: 84, // 7 years
        maxAgeMonths: 120, // 10 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        minAgeMonths: 120, // 10 years
        maxAgeMonths: 144, // 12 years
      },
      {
        value: Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        id: Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        minAgeMonths: 144, // 12 years
      },
      {
        value: Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        id: Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        minAgeMonths: 6,
        maxAgeMonths: 84,
      },
      {
        value: Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
        id: Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
        minAgeMonths: 84,
      },
      {
        value: Constants.dog_class_element_level.akc_futurity,
        id: Constants.dog_class_element_level.akc_futurity,
      },
      {
        value: Constants.dog_class_element_level.akc_sled_dog,
        id: Constants.dog_class_element_level.akc_sled_dog,
      },
    ].filter(option => isElementAvailable(option.value));

    const formRows: IEventRegisterRow[] = [];

    // Add regular conformation section (single select)
    if (regularConformationOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation,
            multiSelect: false,
            options: regularConformationOptions,
          },
        ],
      });
    }

    // Add special conformation section (multi-select)
    if (specialConformationOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation_special_classes,
            multiSelect: true,
            options: specialConformationOptions,
          },
        ],
      });
    }

    // Only add non-licensed section if there are available options
    if (nonLicensedOptions.length > 0) {
      formRows.push({
        allowDuplicates: false,
        inputs: [
          {
            type: "checkbox",
            label: Constants.dog_class_element.conformation_non_licensed,
            multiSelect: true,
            options: nonLicensedOptions,
          },
        ],
      });
    }

    return formRows;
  };

  // Keep the original form for backward compatibility
  export const form: IEventRegisterRow[] = getForm();
}
